import { Box, Button } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import RffTextField from "../../components/finalForm/inputs/RffTextField";

import { DownloadOutlined, TuneRounded } from "@mui/icons-material";
import { isArray, isEmpty, isNil, size } from "lodash";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import LocalStorageOptionRenderer from "../../_src_shared/components/LocalStorageOptionRenderer";
import LocalStorageQuickSaveButton from "../../_src_shared/components/LocalStorageQuickSaveButton";
import RffSelectRadioField from "../../components/finalForm/inputs/RffSelectRadioField";
import RffForm from "../../components/finalForm/RffForm";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import RffTestingUtils from "../../components/finalForm/shared/RffTestingUtils";
import FileUploadField from "../../components/inputs/FileUploadField";
import Flx from "../../components/layout/Flx";
import ScreenContent from "../../components/layout/ScreenContent";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import TitledCard from "../../components/ui/TitledCard/TitledCard";
import { selectOptionsPipelineType } from "../../constants/selectOptions/selectOptionsPipelineType";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanPipelineClear } from "../../redux/actions/loanPipelineActions";
import BorrowersSection from "./BorrowersSection";
import BorrowingEntitiesSection from "./BorrowingEntitiesSection";
import FileUploadSection from "./FileUploadSection";
import LoanDetailSection from "./LoanDetailSection";
import PropertiesSection from "./PropertiesSection";
import { useEnv } from "../../hooks/useEnv";

const lsPrefix = "loanQuickIntakeFormScreenSaves";

const LoanQuickIntakeFormScreen = () => {
  const { isResfin } = useEnv();
  const defaultValues = useMemo(() => {
    // return ex;
    return {
      borrowers: [{}],
      subjectProperties: [{}],
      pipeline: "Underwriting",
    };
  }, []);

  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();

  const quickSave = localStorage.getItem(`${lsPrefix}-quickSave`);
  const storedLayouts = localStorage.getItem(lsPrefix);

  const [quickSaveData, setQuickSaveData] = useState(
    quickSave ? JSON.parse(quickSave) : null
  );

  const [templates, setTemplates] = useState(
    storedLayouts ? JSON.parse(storedLayouts) : []
  );

  const [formData, setFormData] = useState(quickSaveData);
  const { loading, createLoan, createLoanWithFileUploads } =
    useUnderwritingHook();

  const navigate = useNavigate();

  const handleCreateLoanSuccess = () => {
    resetFormData();
    localStorage.removeItem(`${lsPrefix}-quickSave`);
    setSuccess(true);
    dispatch(loanPipelineClear());
    navigate("/loans");
  };

  const onFormSubmit = (formData) => {
    let data = { ...formData };
    let loanName = formData?.loanName;
    if (isNil(loanName) && isArray(data?.subjectProperties)) {
      const propertyName = data?.subjectProperties[0]?.address?.fullAddress;
      if (propertyName) {
        loanName = propertyName;
      }
    }

    data.loanName = loanName;

    // handleCreateLoanSuccess();
    // return;

    // console.log("Submitting loan data:", data);
    // return;
    if (isArray(data?.uploadMetadata) && size(data?.uploadMetadata) >= 1) {
      createLoanWithFileUploads({
        data: data,
        onSuccessFn: (d) => {
          localStorage.removeItem(`${lsPrefix}-quickSave`);
          handleCreateLoanSuccess();
        },
      });
    } else {
      createLoan({
        data: data,
        onSuccessFn: (d) => {
          handleCreateLoanSuccess();
        },
      });
    }
  };

  const resetFormData = useCallback(() => {
    setFormData(defaultValues);
    setQuickSaveData(null);
  }, [defaultValues]);

  const initialState = useMemo(() => {
    if (isNil(formData)) {
      return defaultValues;
    }
    return formData;
  }, [formData]);

  return (
    <>
      <RffForm
        // formSpy
        className={"required"}
        loading={loading}
        success={success}
        submitText="Create Loan"
        maxWidth={"md"}
        sx={{
          pb: 6,
          background: "#f5f8fa",
          ".titled-group-body-root": {
            p: 2.5,
          },
          ".titled-group-header-root": {
            // mb: 1,
            "h1, svg": {
              color: "var(--titleColor)",
            },
          },
          h4: {
            fontWeight: 500,
            fontSize: "17px !important",
          },
        }}
        startContent={
          <Box
            sx={{
              background: "#ffffff",
              pt: 3,
              pb: 3,
              // borderBottom: `1px solid ${blueGrey[50]}`,
              boxShadow: "0 0 0px 1px #dde0e4",
            }}
          >
            <ScreenContent suppressPaddingTopBottom>
              <Flx column gap={3}>
                <Flx fw jb ac>
                  <Flx column gap={1}>
                    <Htag h1>Create New Loan</Htag>
                    <Txt body2>
                      Upload the quick intake excel template, or fill out the
                      following questions.
                    </Txt>
                  </Flx>

                  <LocalStorageQuickSaveButton
                    lsPrefix={lsPrefix}
                    resetFormData={resetFormData}
                    onSaveUpdated={setTemplates}
                    quickSaveData={quickSaveData}
                    onUpdated={setQuickSaveData}
                    initialValues={initialState}
                    // initialValues={defaultValues}
                  />
                </Flx>
                <TemplatesRenderer
                  lsPrefix={lsPrefix}
                  saves={templates}
                  onUpdate={setTemplates}
                />
              </Flx>
            </ScreenContent>
          </Box>
        }
        initialValues={initialState}
        onSubmit={onFormSubmit}
        formTitle={"Create Loan"}
        formDescription="Please provide as much information as possible regarding your loan, borrowers, and properties."
        finalStepLabel="Submit"
      >
        <Flx column gap={4} sx={{ p: 2 }}>
          {isResfin ? null : <UploadExcelContent setFormData={setFormData} />}
          <LoanDetailSection />
          <BorrowersSection />
          <PropertiesSection />
          <BorrowingEntitiesSection />
          <FileUploadSection />
          <TitledCard
            icon={<TuneRounded className="thin" />}
            variant="h1"
            title="Loan Configuration"
          >
            <RffGroup suppressBottomMargin>
              <RffTextField
                name="loanName"
                label="What do you want this loan to be called?"
                // helperText="By default, this will be the property address"
              />

              <RffSelectRadioField
                name="pipeline"
                label="Select a pipeline to place this loan in"
                // helperText="(Optional)"
                options={selectOptionsPipelineType}
              />
            </RffGroup>
          </TitledCard>
        </Flx>
      </RffForm>
    </>
  );
};

const TemplatesRenderer = ({ saves, onUpdate }) => {
  if (isEmpty(saves)) {
    return null;
  }

  return (
    <Flx ac gap={1}>
      <Txt sx={{ flexShrink: 0 }}>Saved Templates:</Txt>
      <LocalStorageOptionRenderer
        saves={saves}
        lsPrefix={lsPrefix}
        onUpdate={onUpdate}
      />
    </Flx>
  );
};

const DownloadLoanUploadTemplate = ({ children }) => {
  const { loading, downloadLoanUploadTemplate } = useLoanFilesHook();

  const onClick = () => {
    downloadLoanUploadTemplate({});
  };
  return (
    <Button
      loading={loading}
      fullWidth={false}
      variant="text"
      // variant="outlined"
      onClick={onClick}
      startIcon={<DownloadOutlined />}
    >
      Download Excel Template
    </Button>
  );
};

const UploadExcelContent = ({ setFormData }) => {
  const { loading, populateLoanFormFromExcel } = useUnderwritingHook();

  const onUpload = (excel) => {
    populateLoanFormFromExcel({
      excel: excel[0],
      onSuccessFn: (data) => {
        setFormData(data);
      },
    });
  };
  return (
    <>
      <Box
        sx={{
          // borderBottom: `1px solid ${grey[400]}`,
          pt: 2,
          // pb: 4,
        }}
      >
        <Flx fw jb ac>
          <Txt>Quick Intake Excel Template Upload</Txt>
          <DownloadLoanUploadTemplate />
        </Flx>
        <FileUploadField
          suppressGrid
          sx={{
            width: "100%",
          }}
          onFilesAdded={onUpload}
          suppressFileList
          loading={loading}
          borderSize={3}
          minHeight="100px"
          suppressUploadButton
          uploadText={"Upload Quick Intake Loan Template Excel"}
        />
      </Box>
      {/* <Divider /> */}
    </>
  );
};
// const ex = {
//   borrowers: [
//     {
//       ff_sold: {
//         last_two_years: 1,
//         two_to_four_years: 0,
//         greater_than_four_years: 0,
//       },
//       ff_retained_as_rental: {
//         last_two_years: 0,
//         two_to_four_years: 0,
//         greater_than_four_years: 0,
//       },
//       income_producing_properties: {
//         last_two_years: 1,
//         two_to_four_years: 0,
//         greater_than_four_years: 0,
//       },
//       guc_sold: {
//         last_two_years: 0,
//         two_to_four_years: 0,
//         greater_than_four_years: 0,
//       },
//       guc_retained_as_rental: {
//         last_two_years: 0,
//         two_to_four_years: 0,
//         greater_than_four_years: 0,
//       },
//       general_contractor_projects: {
//         last_two_years: 0,
//         two_to_four_years: 0,
//         greater_than_four_years: 0,
//       },
//       firstName: "Jalen",
//       lastName: "Brunson",
//       emailAddress: "jb@knicks.com",
//       phone: "5555555555",
//       dateOfBirth: "1995-06-23T04:00:00.000Z",
//       citizenshipStatus: "US Citizen",
//       borrowerHasSSN: true,
//       primaryAddress: {
//         id: "TYfOqjN1nuCv9DltDJB36w-1749061399201",
//         fullAddress: "252 Broadway, Amityville, NY 11701",
//         streetNumber: "252",
//         streetName: "Broadway",
//         zip: "11701",
//         county: "NY",
//         state: "New York",
//         city: "Amityville",
//         country: "United States",
//         countryCode: "US",
//         latitude: 40.681019,
//         longitude: -73.4171607,
//         coordinateTuple: [40.681019, -73.4171607],
//         score: 0.8078479608605978,
//       },
//       maritalStatus: "Married",
//       borrowerSSN: "123456789",
//       cashLiquidity: 250000,
//       otherLiquidity: 100000,
//       estimatedFICO: "Greater than 720",
//       application_felony_conviction: false,
//       application_financial_fraud_misdemeanor: false,
//       bankruptcy_flag: false,
//       firstTimeHomebuyer: false,
//       open_liens_judgments: false,
//       housing_events: false,
//       hasExperienceWithIncomeProducingProperties: true,
//       hasExperienceWithFixFlips: true,
//       hasExperienceWithGroundUpConstruction: true,
//       borrowerExperienceSummary: "Also good basketball player",
//     },
//   ],
//   subjectProperties: [
//     {
//       address: {
//         id: "G2m1YLtOvt7L8WgSBHVx_w-1749061456918",
//         fullAddress: "New York, NY 10001",
//         streetNumber: "",
//         streetName: "",
//         zip: "10001",
//         county: "NY",
//         state: "New York",
//         city: "New York",
//         country: "United States",
//         countryCode: "US",
//         latitude: 40.7483136,
//         longitude: -73.9940525,
//         coordinateTuple: [40.7483136, -73.9940525],
//         score: 1,
//       },
//       asIsValue: 2000000,
//       arv: 2700000,
//       constructionBudget: 1000000,
//       purchasePrice: 1800000,
//       purchaseDate: "2025-05-13T04:00:00.000Z",
//       propertyType: "Commercial",
//       propertyRuralIndicator: false,
//       floodZone: false,
//       commercialSqFootage: 820000,
//       residentialSqFootage: 0,
//       ffPropertyStatus: "Above Average condition - requires limited renovation",
//     },
//   ],
//   pipeline: "Commercial",
//   salesperson: "joliva@cliffcomortgage.com",
//   permittedUsers: [],
//   loanProductType: "Fix and Flip",
//   loanPurpose: "Purchase",
//   exitStrategy: "Sell it all",
//   expectedClosingDate: "2025-06-30T04:00:00.000Z",
//   baseLoanAmount: 337500,
//   totalHoldback: 81000,
//   totalConstructionBudget: 416500,
//   loanName: "New York, NY 10001",
//   borrowerEntity: {
//     applicantHasExistingEntity: true,
//     entityName: "MSG",
//     applicantEntityPercentOwnership: 1,
//     entityStateOfOrganization: "NY",
//     entityType: "LLC",
//     entityOrganizationDate: "2025-06-18T04:00:00.000Z",
//     entityAssets: true,
//     entityEINNumber: "74563456",
//   },
// };
export default LoanQuickIntakeFormScreen;
